// Sell — the point of sale (spec/13 §4, F-POS-09/-01/-12). One cart for everything a venue sells,
// laid out the way a counter works (the Square pattern): three shelves — **All**, **Food & drinks** and
// **Passes** — over a grid of cards you tap to add, and a cart column with its lines, the station it goes
// to, the total in tabular figures and one big Charge. Inside Food & drinks, the menu's categories are
// filter chips over the grid and headings within it (the owner's 2026-10-01 rework: one tab per menu
// category did not scale). A card shows the product's picture (F-FNB-01) or its tinted initial, its name,
// its price and whether it can be sold now, and says how many are in the cart once one is. Charging opens
// checkout (./Checkout) in a centred dialog that fits a normal sale without scrolling: the total, the payment
// method, cash received with its change, taken one or more times until the sale is paid. Nothing on this screen slides in from the side (2026-10-07): a custom line, a dish's
// modifiers, starting a shift and checkout are each a dialog over the counter.
//
// The client only chooses what to sell and how it is paid; the server prices every line, computes the
// GST and settles to the ledger (spec/13 §4). Charging creates the sale (F-POS-01), then each tender
// is a `POST …/payments` (F-POS-12) until the total is covered — cash shows change, counter UPI takes
// an optional reference. `orders:create` is needed to sell; the server checks it on every request.
//
// A player can be attached to the cart (spec/06 §8.3 step 1): the sale is created with their id, and
// checkout then also offers the player's own tenders — **Wallet** (a `wallet` payment that draws their
// balance, shown beside it; F-WAL-04) and **On account** (charged to what they owe, `orders:on_account`;
// F-POS-07). An anonymous sale offers only cash and counter UPI.
//
// Where the venue's online gates are open (`payments.onlineEnabled` and a payment provider, spec/06
// §9.5), checkout also offers **Online (UPI)** (F-PAY-03): it asks the provider for a payment request for
// the amount, shows the UPI link and QR content, and polls it; the sale records the `online` payment
// only when the provider confirms it, and is read again then.
//
// Before the first tender, checkout takes a **discount code** (F-POS-02, `applyDiscountCode`): the server
// re-prices the sale and the total follows. With a player attached it also redeems a **coupon** for them
// (F-POS-06, `redeemCoupon`): a coupon grants a wallet bonus rather than a discount, which the Wallet
// tender can then spend. A manual staff discount stays on the Orders screen's order dialog (F-POS-05).
//
// **Passes** (F-POS-01, F-PRC-01) sit on their own shelf beside the menu (and under All): the venue's passes sold at the
// counter (`passes/shelf.ts`), each with its price, play time and validity. A tap adds a pass line — one pass
// per line, so two taps are two passes, each its own holding — and a pass belongs to a player, so Charge waits
// until one is attached. The server prices the line from the pass product (the client names it only) and
// issues the holding when the sale is paid: a pass sale is an ordinary sale in Orders, with its tenders, its
// receipt and any discount code. The Passes tab's Sell opens this screen with `?pass=…&player=…` preloaded.
//
// **Promotions before the sale is placed** (F-POS-03): while the cart has lines, it is quoted by the server
// (`quoteSale`, the same pricing the sale takes, nothing written) once it rests for a moment. The cart then
// shows its subtotal, each automatic promotion that applies as its own line, and the total they leave. The
// last quote stays up while the next loads; a refused quote falls back to the cart's own sum without a
// word, since placing the sale is the authority. A discount code is applied at checkout, after placing, so
// the quote never carries one. The quoted total goes with the sale as `expectedTotal` (F-SES-17 rule 3): if the
// price moved in between, the server places nothing and answers `409 price-changed` with the fresh total, which the
// cart shows before Charge is pressed again.
//
// **Selling time into a running session** (F-SES-17): the station modal's "Sell a pass" opens Sell with the
// player and their session (`?player=…&session=…`). While that player is attached, the cart says the pass is added
// to the session on that PC — it starts funding the moment it is paid — and the sale carries the session. A pass for
// another class than the PC's is still sold, with a warning that it is not usable there and waits on the account.
//
// **Awaiting payment** (F-POS-10): kiosk and app orders placed "Pay at the counter" sit in a queue at the top of the
// screen, live over the orders channel; Take payment opens one in checkout, Reject cancels it (`AwaitingPayment.tsx`).
//
// **Guest passes** (F-SES-07): where the venue serves guests (`sessions.guestsEnabled`, read from `GET …/guest-passes`
// so a cashier needs no settings), a cart with passes and no player can be sold as guest passes — "Sell as guest
// passes" under the cart's notice. Each pass line goes as `guest: true` on a sale that names nobody, so only cash, card
// and counter UPI take it; once it is paid, checkout shows each pass's ticket code big, with a QR to keep the time in an
// account (`guests/GuestTicket.tsx`).

import type { CreateSaleLine, MenuProduct, Money, OrganizationPlayer, PassProduct, QuoteSaleRequest, Sale, SaleQuote } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import {
  addToCartFeedback,
  Avatar,
  Button,
  CartBadge,
  Checkbox,
  cx,
  Dialog,
  EmptyState,
  RollingNumber,
  SegmentedControl,
  SelectField,
  Skeleton,
  TextField,
  useCollapsingList,
} from '@clavian/ui-web';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { useAccess } from '../../api/queries';
import { PROBLEM, problemCode, problemDetail, problemTotal } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon, NavIcon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { useStations } from '../../stations/queries';
import { CardsSkeleton, Forbidden, Notice, Page, SearchField } from '../../ui';
import { TickingNumber } from '../../ui/ticking';
import { DietMarker } from '../diet';
import { moneyFrom } from '../money';
import { useGuestPasses } from '../guests/queries';
import { usePassProducts } from '../passes/queries';
import { counterShelf, isMembersOnly, passDuration, validitySummary } from '../passes/shelf';
import { pickerResults, PLAYER_SEARCH_MIN, playerName, usePlayerProfile, usePlayerSearch } from '../players/queries';
import { ProductArt } from '../product-art';
import { DayStatusStrip, localDateLabel, useVenueBusinessDay } from '../shifts/days-and-shifts';
import { StartShiftFlow } from '../shifts/ShiftFlows';
import { type ModifierChoice, ModifierDialog } from './ModifierDialog';
import { useCreateSale, useSaleQuote, useVenueMenu } from './queries';
import { AwaitingPaymentQueue } from './AwaitingPayment';
import { CheckoutDialog } from './Checkout';
import { useSession } from '../sessions/queries';

import './sell.css';

const ORDERS_CREATE = 'orders:create';
const ORDERS_ON_ACCOUNT = 'orders:on_account';
const ORDERS_VOID = 'orders:void';
const PASSES_READ = 'passes:read';
const ALL = 'all';
/** The shelves (spec/13 §4): everything, the menu, and the counter's passes. */
const FOOD = 'food';
const PASSES = 'passes';
type Shelf = typeof ALL | typeof FOOD | typeof PASSES;
/** The channel a product must be sold on to be added at the counter (spec/13 §6.1 `channels`). */
const COUNTER = 'counter';

/** A line in the working cart, before the server prices it. */
interface CartLine {
  readonly key: string;
  readonly kind: CreateSaleLine['kind'];
  readonly productId?: string;
  /** The product's chosen modifiers (F-FNB-01), sent with the line; the server prices them. */
  readonly modifierIds?: readonly string[];
  /** A pass line (F-POS-01): the pass product; always one pass, priced by the server. */
  readonly passProductId?: string;
  /** For a pass line, its play time — shown under the name. */
  readonly detail?: string;
  readonly name: string;
  readonly unitPrice: Money;
  readonly quantity: number;
}

export function SellPage(): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const format = useVenueFormatter();
  const venue = useCurrentVenue();
  const currency = venue.currency ?? 'INR';
  const access = useAccess({ venueId: venue.id });
  const canSell = access.status === 'ready' && access.can(ORDERS_CREATE);
  const canOnAccount = access.status === 'ready' && access.can(ORDERS_ON_ACCOUNT);
  const canVoid = access.status === 'ready' && access.can(ORDERS_VOID);
  // Whether the venue serves guests (F-SES-07): read beside its guest passes, which the front desk may read.
  const guestList = useGuestPasses(venue.organizationId, venue.id, { enabled: access.status === 'ready' && access.can(PASSES_READ) });
  const guestsEnabled = guestList.data?.guestsEnabled === true;
  /** A pending kiosk/app order opened from the queue (F-POS-10): checked out on its own, the cart left as it is. */
  const [queued, setQueued] = useState<Sale | null>(null);
  /** "Start shift — count the float" from the status strip (ADR-0029), without leaving the counter. */
  const [startingShift, setStartingShift] = useState(false);
  const businessDay = useVenueBusinessDay(venue);

  const menu = useVenueMenu(venue.organizationId, venue.id);
  const passProducts = usePassProducts(venue.organizationId, venue.id);
  const passes = useMemo(() => counterShelf(passProducts.data ?? [], venue.id, new Date()), [passProducts.data, venue.id]);

  const [cart, setCart] = useState<readonly CartLine[]>([]);
  const [stationId, setStationId] = useState('');
  const [player, setPlayer] = useState<OrganizationPlayer | null>(null);
  const [sale, setSale] = useState<Sale | null>(null);
  const [shelf, setShelf] = useState<Shelf>(ALL);
  /** The Food & drinks shelf's category chip: every category, or one menu category's id. */
  const [category, setCategory] = useState<string>(ALL);
  const [query, setQuery] = useState('');
  const [customOpen, setCustomOpen] = useState(false);
  /** "Sell as guest passes" (F-SES-07): the cart's passes go to walk-ins with no account, each with a ticket code. */
  const [guestSale, setGuestSale] = useState(false);

  const createSale = useCreateSale(venue.organizationId, venue.id);
  const [chargeError, setChargeError] = useState<string | undefined>(undefined);

  const estimatedTotal = useMemo<Money>(
    () => ({ amount: cart.reduce((sum, line) => sum + line.unitPrice.amount * line.quantity, 0), currency }),
    [cart, currency],
  );
  const itemCount = cart.reduce((sum, line) => sum + line.quantity, 0);
  // A removed line collapses before it goes (200 ms; at once under reduced motion).
  const cartEntries = useCollapsingList(cart, (line) => line.key);

  /** The product the modifiers dialog is open for (F-FNB-01): one with modifier groups is chosen before it is added. */
  const [choosing, setChoosing] = useState<MenuProduct | null>(null);

  function addProduct(product: MenuProduct): void {
    if (product.modifierGroups.some((group) => group.modifiers.length > 0)) {
      setChoosing(product);
      return;
    }
    addLine(product, { modifierIds: [], names: [], extra: { amount: 0, currency: product.price.currency } });
  }

  /** The product with its chosen modifiers: the same choice again adds to that line, a different one is its own. */
  function addLine(product: MenuProduct, choice: ModifierChoice): void {
    const key = `p:${product.id}:${[...choice.modifierIds].sort().join(',')}`;
    setCart((current) => {
      const existing = current.find((line) => line.key === key);
      if (existing !== undefined) {
        return current.map((line) => (line.key === existing.key ? { ...line, quantity: line.quantity + 1 } : line));
      }
      return [
        ...current,
        {
          key,
          kind: 'food',
          productId: product.id,
          ...(choice.modifierIds.length === 0 ? {} : { modifierIds: choice.modifierIds, detail: choice.names.join(', ') }),
          name: product.name,
          unitPrice: { amount: product.price.amount + choice.extra.amount, currency: product.price.currency },
          quantity: 1,
        },
      ];
    });
  }

  /** A pass is one line per pass: each is its own holding, with its own expiry and price paid. */
  function addPass(pass: PassProduct): void {
    setCart((current) => [...current, passLine(pass, `pass:${pass.id}:${current.length}:${Date.now()}`, passDuration(t, pass.version.durationSeconds))]);
  }

  // The Passes tab's Sell opens this screen with the pass and the player preloaded (`?pass=…&player=…`). Each
  // is taken once — while rendering, as soon as its data is here — then dropped from the address, so a reload
  // or a finished sale does not add it again.
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { readonly pass?: unknown; readonly player?: unknown; readonly session?: unknown };
  const preloadPass = typeof search.pass === 'string' ? search.pass : undefined;
  const preloadPlayer = typeof search.player === 'string' ? search.player : undefined;
  // The player's running session, from the station modal (F-SES-17). Kept for as long as that player is attached.
  const [intoSessionId, setIntoSessionId] = useState<string | undefined>(() => (typeof search.session === 'string' ? search.session : undefined));
  const [pending, setPending] = useState<{ readonly pass: string | undefined; readonly player: string | undefined }>(() => ({ pass: preloadPass, player: preloadPlayer }));
  const preloadedPlayer = usePlayerProfile(venue.organizationId, pending.player);
  if (pending.pass !== undefined && (passProducts.data !== undefined || passProducts.isError)) {
    const wanted = pending.pass;
    setPending((current) => ({ ...current, pass: undefined }));
    const found = passes.find((pass) => pass.id === wanted && !isMembersOnly(pass));
    if (found === undefined) {
      setChargeError(t('sell.passes.preloadUnavailable'));
    } else {
      setCart((current) => [...current, passLine(found, `pass:${found.id}:preloaded`, passDuration(t, found.version.durationSeconds))]);
      setShelf(PASSES);
    }
  }
  if (pending.player !== undefined && (preloadedPlayer.data !== undefined || preloadedPlayer.isError)) {
    setPending((current) => ({ ...current, player: undefined }));
    if (preloadedPlayer.data !== undefined) {
      const { notes: _notes, ...profile } = preloadedPlayer.data;
      setPlayer(profile);
    }
  }
  const preloadDone = pending.pass === undefined && pending.player === undefined;
  useEffect(() => {
    if (preloadDone && (preloadPass !== undefined || preloadPlayer !== undefined)) void navigate({ to: '.', search: {}, replace: true });
  }, [preloadDone, preloadPass, preloadPlayer, navigate]);

  function addCustom(line: Omit<CartLine, 'key'>): void {
    setCart((current) => [...current, { ...line, key: `c:${current.length}:${Date.now()}` }]);
  }

  function setQuantity(key: string, quantity: number): void {
    setCart((current) => (quantity <= 0 ? current.filter((line) => line.key !== key) : current.map((line) => (line.key === key ? { ...line, quantity } : line))));
  }

  function reset(): void {
    setCart([]);
    setStationId('');
    setPlayer(null);
    setSale(null);
    setChargeError(undefined);
    setIntoSessionId(undefined);
    setGuestSale(false);
  }

  // The session a pass is sold into (F-SES-17): only while it runs, and only for the player it belongs to.
  const intoSessionQuery = useSession(venue.organizationId, intoSessionId);
  const intoSession = intoSessionQuery.data !== undefined && intoSessionQuery.data.status !== 'ended' && player !== null && intoSessionQuery.data.playerId === player.id ? intoSessionQuery.data : undefined;
  const allStations = useStations(venue.organizationId, venue.id);
  const sessionStation = intoSession === undefined ? undefined : (allStations.data?.pages ?? []).flatMap((page) => page.data).find((station) => station.id === intoSession.stationId);
  const sessionStationLabel = sessionStation === undefined ? t('sell.intoSession.thisPc') : (sessionStation.name ?? t('sell.cart.toStation', { number: sessionStation.number }));
  /** Passes in the cart that do not cover the session's PC: sold, but they wait on the account (F-SES-17 rule 4). */
  const notUsableHere =
    sessionStation === undefined
      ? []
      : cart.filter((line) => {
          if (line.passProductId === undefined) return false;
          const product = passes.find((pass) => pass.id === line.passProductId);
          return product !== undefined && !product.version.stationClassIds.includes(sessionStation.classId);
        });

  /** A pass belongs to a player: with a pass in the cart and nobody attached, Charge waits (F-POS-01) — unless sold as guest passes. */
  const hasPass = cart.some((line) => line.passProductId !== undefined);
  /** Guest passes (F-SES-07): only where the venue serves guests, and only on a sale that names nobody. */
  const asGuests = guestSale && guestsEnabled && hasPass && player === null;
  const needsPlayer = hasPass && player === null && !asGuests;

  /** What the sale would be placed with — the quote prices exactly this (F-POS-03). A pass waiting for its player is not quoted. */
  const saleBody = useMemo<QuoteSaleRequest | null>(
    () =>
      cart.length === 0 || needsPlayer
        ? null
        : { channel: 'counter', ...(stationId === '' ? {} : { stationId }), ...(player === null ? {} : { playerId: player.id }), lines: saleLines(cart, asGuests) },
    [cart, needsPlayer, stationId, player, asGuests],
  );
  const quote = useSaleQuote(venue.organizationId, venue.id, sale === null ? saleBody : null);
  /** The last good quote, while the cart has lines; a refused one falls back to the cart's own sum. */
  const priced: SaleQuote | undefined = cart.length > 0 && !quote.isError ? quote.data : undefined;
  const dueTotal = priced?.total ?? estimatedTotal;

  function charge(): void {
    if (saleBody === null) return;
    setChargeError(undefined);
    createSale.mutate(
      {
        ...saleBody,
        // Into the player's running session (F-SES-17): the pass is queued onto it the moment the sale is paid.
        ...(hasPass && intoSession !== undefined ? { sessionId: intoSession.id, ...(stationId === '' ? { stationId: intoSession.stationId } : {}) } : {}),
        // The total the cart showed, compared by the server — never a price (F-SES-17 rule 3).
        ...(priced === undefined ? {} : { expectedTotal: priced.total }),
      },
      {
        onSuccess: (created) => setSale(created),
        onError: (error) => {
          if (problemCode(error) === PROBLEM.priceChanged) {
            // The price moved since the cart was quoted: show the new total and let the cashier charge again.
            const fresh = problemTotal(error);
            setChargeError(fresh === undefined ? t('sell.charge.priceChangedPlain') : t('sell.charge.priceChanged', { amount: format.money(fresh) }));
            void quote.refetch();
            return;
          }
          if (problemCode(error) !== PROBLEM.conflict) {
            setChargeError(problem.text(error, { fallback: t('sell.charge.refused') }));
            return;
          }
          // A pass the counter cannot sell now names its reason (off sale, a limit reached, members only).
          setChargeError(hasPass ? (problemDetail(error) ?? t('sell.charge.unavailable')) : t('sell.charge.unavailable'));
        },
      },
    );
  }

  const inCart = new Map<string, number>();
  for (const line of cart) if (line.productId !== undefined) inCart.set(line.productId, (inCart.get(line.productId) ?? 0) + line.quantity);
  const passesInCart = new Map<string, number>();
  for (const line of cart) if (line.passProductId !== undefined) passesInCart.set(line.passProductId, (passesInCart.get(line.passProductId) ?? 0) + 1);

  let catalogue: ReactNode;
  if (menu.data === undefined) {
    if (menu.isError && problemCode(menu.error) === PROBLEM.forbidden) {
      catalogue = <Forbidden message={t('sell.notPermitted')} />;
    } else {
      catalogue = menu.isError ? <LoadFailure error={menu.error} message={t('sell.unavailable')} onRetry={() => void menu.refetch()} /> : <CardsSkeleton count={8} height="12rem" />;
    }
  } else {
    const visible = menu.data.products.filter((product) => product.visible);
    const categories = menu.data.categories
      .filter((item) => item.visible && visible.some((product) => product.categoryId === item.id))
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const needle = query.trim().toLowerCase();
    const matching = visible.filter((product) => needle === '' || product.name.toLowerCase().includes(needle)).sort((a, b) => a.sortOrder - b.sortOrder);
    // The menu under its categories, in the menu's order; a product whose category is hidden sits under "Other".
    const known = new Set(categories.map((item) => item.id));
    const groups = [
      ...categories.map((item) => ({ id: item.id, name: item.name, products: matching.filter((product) => product.categoryId === item.id) })),
      { id: 'other', name: t('sell.otherCategory'), products: matching.filter((product) => !known.has(product.categoryId)) },
    ].filter((group) => group.products.length > 0 && (shelf !== FOOD || category === ALL || group.id === category));
    const shownPasses = passes.filter((pass) => needle === '' || pass.version.name.toLowerCase().includes(needle));
    const showFood = shelf !== PASSES;
    const noMatches = <EmptyState size="compact" title={t('common.noMatches', { query: query.trim() })} />;

    let foodArea: ReactNode = null;
    if (showFood) {
      if (visible.length === 0) {
        // An empty menu says so on its own shelf, and on All when there is nothing else to sell.
        if (shelf === FOOD || passes.length === 0) {
          foodArea = <EmptyState icon={<Icon name="cup" />} title={t('sell.noProductsTitle')} description={t('sell.noProducts')} />;
        }
      } else if (groups.length === 0) {
        if (shelf === FOOD || shownPasses.length === 0) foodArea = noMatches;
      } else {
        foodArea = groups.map((group) => (
          <section key={group.id} className="console-sell-group" aria-labelledby={`sell-group-${group.id}`}>
            <h3 className="console-sell-group__title" id={`sell-group-${group.id}`}>
              {group.name}
              <span className="console-sell-group__count console-num">{group.products.length}</span>
            </h3>
            <div className="console-sell-grid" role="list" aria-label={group.name}>
              {group.products.map((product) => (
                <ProductTile key={product.id} product={product} quantity={inCart.get(product.id) ?? 0} onAdd={() => addProduct(product)} disabled={!canSell} />
              ))}
            </div>
          </section>
        ));
      }
    }

    let passArea: ReactNode = null;
    if (shelf === PASSES && shownPasses.length === 0) {
      passArea = needle === '' ? <EmptyState icon={<NavIcon name="passes" />} title={t('sell.passes.noneTitle')} description={t('sell.passes.none')} /> : noMatches;
    } else if (shelf !== FOOD && shownPasses.length > 0) {
      passArea = (
        <section className="console-sell-group" aria-labelledby="sell-group-passes">
          <h3 className="console-sell-group__title" id="sell-group-passes">
            {t('sell.passes.label')}
            <span className="console-sell-group__count console-num">{shownPasses.length}</span>
          </h3>
          <div className="console-sell-grid console-sell-grid--passes" role="list" aria-label={t('sell.passes.label')}>
            {shownPasses.map((pass) => (
              <PassTile key={pass.id} pass={pass} quantity={passesInCart.get(pass.id) ?? 0} onAdd={() => addPass(pass)} disabled={!canSell} />
            ))}
          </div>
        </section>
      );
    }

    catalogue = (
      <>
        <div className="console-sell__filters">
          <SegmentedControl<Shelf>
            label={t('sell.shelf.label')}
            value={shelf}
            onValueChange={(next) => {
              setShelf(next);
              setCategory(ALL);
            }}
            className="console-sell__shelves"
            options={[
              { value: ALL, label: t('sell.shelf.all'), count: visible.length + passes.length },
              { value: FOOD, label: t('sell.shelf.food'), count: visible.length },
              ...(passes.length > 0 ? [{ value: PASSES as Shelf, label: t('sell.shelf.passes'), count: passes.length }] : []),
            ]}
          />
          <SearchField label={t('sell.search')} value={query} onChange={(event) => setQuery(event.target.value)} />
        </div>
        {shelf === FOOD && categories.length > 1 ? (
          <div className="console-sell-chips" role="group" aria-label={t('sell.categories')}>
            {[{ id: ALL, name: t('sell.allCategories') }, ...categories].map((item) => (
              <button key={item.id} type="button" className="console-sell-chip" aria-pressed={category === item.id} onClick={() => setCategory(item.id)}>
                {item.name}
              </button>
            ))}
          </div>
        ) : null}
        {foodArea}
        {passArea}
        {showFood && canSell ? (
          <div className="console-sell-grid" role="list" aria-label={t('sell.custom.title')}>
            <div role="listitem">
              <button type="button" className="console-sell-custom" onClick={() => setCustomOpen(true)}>
                <span className="console-sell-custom__icon" aria-hidden="true">
                  <Icon name="plus" />
                </span>
                <span className="console-sell-custom__text">
                  <span className="console-sell-card__name">{t('sell.custom.title')}</span>
                  <span className="console-subtle">{t('sell.custom.hint')}</span>
                </span>
              </button>
            </div>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <Page width="wide" title={t('sell.title')}>
      <Dialog open={startingShift} onOpenChange={setStartingShift} size="md" dirty="auto" title={t('shifts.flow.startTitle')} description={t('shifts.flow.startHint')}>
        {startingShift ? (
          <StartShiftFlow
            currency={currency}
            organizationId={venue.organizationId}
            venueId={venue.id}
            dayLabel={businessDay === undefined ? '' : localDateLabel(businessDay.businessDay, format.locale)}
            onClose={() => setStartingShift(false)}
          />
        ) : null}
      </Dialog>
      {/* A counter (2026-10-07 uplift): the cart stands in its own column from the top of the page, so Charge is in
          reach from the first moment. The day's one-line status, the queue and the shelves stack beside it; the
          counters board lives on Shifts (2026-10-08: the drawer is known from the open shift). */}
      <div className="console-sell">
        <div className="console-sell__main">
          <DayStatusStrip venue={venue} onStartShift={() => setStartingShift(true)} />
          {access.status === 'ready' && !canSell ? <Notice role="note">{t('sell.readOnly')}</Notice> : null}
          <AwaitingPaymentQueue organizationId={venue.organizationId} venueId={venue.id} canTake={canSell} canReject={canVoid} onTake={setQueued} />
          <section className="console-sell__catalogue" aria-label={t('sell.productsLabel')}>
            {catalogue}
          </section>
        </div>

        <aside className="console-sell__cart" aria-label={t('sell.cart.label')}>
          <header className="console-sell__cart-head">
            <h2 className="console-sell__cart-title">
              {t('sell.cart.title')} <CartBadge count={itemCount} />
            </h2>
            {cart.length > 0 ? (
              <Button variant="ghost" size="sm" onClick={reset}>
                {t('sell.cart.clear')}
              </Button>
            ) : null}
          </header>
          {cart.length === 0 ? (
            <div className="console-sell__cart-empty">
              <span className="console-sell__cart-empty-icon" aria-hidden="true">
                <Icon name="card" />
              </span>
              <p>{t('sell.cart.empty')}</p>
            </div>
          ) : (
            <ul className="console-cart-lines">
              {cartEntries.map(({ item: line, key, leaving }) => (
                <CartRow key={key} line={line} leaving={leaving} guest={asGuests} onQuantity={(q) => setQuantity(line.key, q)} />
              ))}
            </ul>
          )}

          <div className="console-sell__cart-foot">
            {needsPlayer ? (
              <Notice tone="warn" role="status">
                {t('sell.passes.needsPlayer')}
              </Notice>
            ) : null}
            {asGuests ? (
              <Notice tone="info" role="status">
                {t('sell.passes.guest.on')}
              </Notice>
            ) : null}
            {/* Guest passes (F-SES-07): offered for passes with nobody attached, where the venue serves guests. */}
            {hasPass && player === null && guestsEnabled ? (
              <div className="console-sell-guest">
                <Checkbox label={t('sell.passes.guest.label')} checked={guestSale} onCheckedChange={(checked) => setGuestSale(checked === true)} aria-describedby="sell-guest-hint" />
                <p className="console-subtle" id="sell-guest-hint">
                  {t('sell.passes.guest.hint')}
                </p>
              </div>
            ) : null}
            {hasPass && intoSession !== undefined ? (
              <Notice tone={notUsableHere.length > 0 ? 'warn' : 'info'} role="status">
                {notUsableHere.length > 0
                  ? t('sell.intoSession.notUsable', { station: sessionStationLabel, names: notUsableHere.map((line) => line.name).join(', ') })
                  : t('sell.intoSession.adds', { station: sessionStationLabel })}
              </Notice>
            ) : null}
            {asGuests ? null : <PlayerField organizationId={venue.organizationId} player={player} onChange={setPlayer} />}
            <StationField stationId={stationId} onChange={setStationId} />
            {priced !== undefined && priced.discount.amount > 0 ? (
              <dl className="console-checkout-totals console-cart-summary" aria-label={t('sell.cart.summary')} aria-busy={quote.isFetching}>
                <div>
                  <dt>{t('sell.cart.subtotal')}</dt>
                  <dd className="console-num">{format.money(priced.gross)}</dd>
                </div>
                {priced.promotions.map((promotion) => (
                  <div key={promotion.promotionId} className="console-cart-summary__promotion">
                    <dt>{promotion.name}</dt>
                    <dd className="console-num">−{format.money(promotion.amount)}</dd>
                  </div>
                ))}
                {priced.discountCode === null ? null : (
                  <div className="console-cart-summary__promotion">
                    <dt>{t('sell.cart.code', { code: priced.discountCode.code })}</dt>
                    <dd className="console-num">−{format.money(priced.discountCode.amount)}</dd>
                  </div>
                )}
              </dl>
            ) : null}
            <div className="console-cart-total">
              <span className="console-cart-total__label">
                {priced === undefined ? t('sell.cart.estimated') : t('sell.cart.total')}
                <span className="console-subtle">{t('sell.cart.items', { count: itemCount })}</span>
              </span>
              <strong className="console-cart-total__value console-num">
                <TickingNumber value={dueTotal.amount} format={(amount) => format.money({ ...dueTotal, amount })} />
              </strong>
            </div>
            {chargeError !== undefined ? (
              <Notice tone="danger" role="alert">
                {chargeError}
              </Notice>
            ) : null}
            <Button
              variant="primary"
              size="lg"
              block
              disabled={!canSell || cart.length === 0 || needsPlayer}
              busy={createSale.isPending}
              onClick={charge}
            >
              {t('sell.charge.button', { amount: format.money(dueTotal) })}
            </Button>
          </div>
        </aside>
      </div>

      <Dialog open={customOpen} onOpenChange={setCustomOpen} size="sm" dirty="auto" title={t('sell.custom.title')} description={t('sell.custom.sheetHint')}>
        {customOpen ? (
          <CustomLineForm
            currency={currency}
            onAdd={(line) => {
              addCustom(line);
              setCustomOpen(false);
            }}
          />
        ) : null}
      </Dialog>

      <ModifierDialog
        product={choosing}
        onClose={() => setChoosing(null)}
        onAdd={(product, choice) => {
          addLine(product, choice);
          setChoosing(null);
        }}
      />

      <CheckoutDialog
        sale={sale}
        player={player}
        canOnAccount={canOnAccount}
        organizationId={venue.organizationId}
        venueId={venue.id}
        onSale={setSale}
        onClose={() => setSale(null)}
        onDone={reset}
      />

      {/* A kiosk/app order taken from the queue (F-POS-10): its own checkout, so the cart being built stays. */}
      <CheckoutDialog
        sale={queued}
        player={null}
        canOnAccount={canOnAccount}
        organizationId={venue.organizationId}
        venueId={venue.id}
        onSale={setQueued}
        onClose={() => setQueued(null)}
        onDone={() => setQueued(null)}
      />
    </Page>
  );
}

/** The cart as the sale's lines: the server prices each (F-POS-01) — a sale and its quote send the same. */
function saleLines(cart: readonly CartLine[], asGuests = false): CreateSaleLine[] {
  return cart.map((line) =>
    line.passProductId !== undefined
      ? // The server prices a pass line from the pass product; the client names the product only. A guest pass line
        // (F-SES-07) is the same pass, for a walk-in with no account.
        { kind: 'time', passProductId: line.passProductId, quantity: 1, ...(asGuests ? { guest: true } : {}) }
      : line.productId !== undefined
        ? { kind: line.kind, productId: line.productId, ...(line.modifierIds === undefined ? {} : { modifierIds: [...line.modifierIds] }), quantity: line.quantity }
        : { kind: line.kind, name: line.name, unitPrice: line.unitPrice, quantity: line.quantity },
  );
}

/** A pass line for the cart: always one pass, priced by the server when the sale is placed (F-POS-01). */
function passLine(pass: PassProduct, key: string, duration: string): CartLine {
  return { key, kind: 'time', passProductId: pass.id, detail: duration, name: pass.version.name, unitPrice: pass.version.price, quantity: 1 };
}

/**
 * A product's card (spec/13 §4, F-FNB-01): its picture or tinted initial, its name and diet marker, its price in
 * tabular figures, and whether it can be sold now — sold out (F-FNB-03), or not sold at the counter (its menu
 * channels). The whole card is the tap target; once in the cart it takes the selection tone and a count badge,
 * which pops again on every add.
 */
function ProductTile({ product, quantity, onAdd, disabled }: { readonly product: MenuProduct; readonly quantity: number; readonly onAdd: () => void; readonly disabled: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const [added, setAdded] = useState(0);
  const soldOut = !product.available;
  const offCounter = !soldOut && !product.channels.includes(COUNTER);
  // Outside its availability schedule, in the venue's time (F-FNB-02): listed, and disabled until its window opens.
  const notNow = !soldOut && !offCounter && !product.inSchedule;
  const blocked = soldOut || offCounter || notNow;
  return (
    <div role="listitem">
      <button
        type="button"
        className={cx('console-sell-card', blocked && 'console-sell-card--unavailable', quantity > 0 && 'console-sell-card--in-cart')}
        disabled={disabled || blocked}
        data-pulse-card
        onClick={(event) => {
          addToCartFeedback(event.currentTarget);
          onAdd();
          setAdded((count) => count + 1);
        }}
        aria-label={
          soldOut
            ? t('sell.soldOutNamed', { name: product.name })
            : offCounter
              ? t('sell.notAtCounterNamed', { name: product.name })
              : notNow
                ? t('sell.notNowNamed', { name: product.name })
                : t('sell.add', { name: product.name })
        }
      >
        <span className="console-sell-card__media" aria-hidden="true">
          <ProductArt id={product.id} name={product.name} image={product.image} sizes="(min-width: 64rem) 12rem, 45vw" className="console-sell-card__art" />
          {quantity > 0 ? (
            <span key={added} className="console-sell-card__qty console-num">
              {t('sell.inCartShort', { count: quantity })}
            </span>
          ) : null}
          {blocked ? <span className="console-sell-card__state">{soldOut ? t('menu.soldOut') : offCounter ? t('sell.notAtCounter') : t('sell.notNow')}</span> : null}
          {/* The add affordance sits on the art's lower corner (ADR-0043), always shown: a tablet has no hover. */}
          {blocked ? null : (
            <span className="console-sell-card__add">
              <Icon name="plus" />
            </span>
          )}
        </span>
        <span className="console-sell-card__body" aria-hidden="true">
          <span className="console-sell-card__name">{product.name}</span>
          <span className="console-sell-card__foot">
            <span className="console-sell-card__price console-num">{format.money(product.price)}</span>
            <DietMarker marker={product.dietMarker} />
          </span>
        </span>
      </button>
    </div>
  );
}

/** A pass's times of day, as its card says them: "10 pm–6 am", "+1 more" when it has other windows. */
function windowSummary(t: (key: string, options?: Record<string, unknown>) => string, windows: PassProduct['version']['windows']): string {
  const first = windows[0];
  if (first === undefined) return t('passes.anytime');
  const span = t('sell.passes.window', { from: clockLabel(first.start), to: clockLabel(first.end) });
  return windows.length > 1 ? t('sell.passes.moreWindows', { span, count: windows.length - 1 }) : span;
}

/** A venue-local "22:00" as the counter says it: "10 pm", "6:30 am", "12 am". */
function clockLabel(local: string): string {
  const [hours = 0, minutes = 0] = local.split(':').map(Number);
  const suffix = hours < 12 ? 'am' : 'pm';
  const hour = hours % 12 === 0 ? 12 : hours % 12;
  return minutes === 0 ? `${hour} ${suffix}` : `${hour}:${String(minutes).padStart(2, '0')} ${suffix}`;
}

/**
 * A pass on the counter shelf (F-POS-01): a ticket, not a dish — its play time large on a tinted band, its name,
 * when in the day it plays and how long it stays valid, any purchase limit, its price, and that it needs a player.
 * A members-only pass shows, but cannot be added: the counter cannot check a membership yet.
 */
function PassTile({ pass, quantity, onAdd, disabled }: { readonly pass: PassProduct; readonly quantity: number; readonly onAdd: () => void; readonly disabled: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const [added, setAdded] = useState(0);
  const terms = pass.version;
  const membersOnly = isMembersOnly(pass);
  const limits = terms.purchaseLimits;
  const limit =
    limits?.perPlayerPerDay !== undefined
      ? t('sell.passes.limitDay', { count: limits.perPlayerPerDay })
      : limits?.perPlayerTotal !== undefined
        ? t('sell.passes.limitTotal', { count: limits.perPlayerTotal })
        : null;
  return (
    <div role="listitem">
      <button
        type="button"
        className={cx('console-sell-card', 'console-sell-card--pass', membersOnly && 'console-sell-card--unavailable', quantity > 0 && 'console-sell-card--in-cart')}
        data-label={terms.label}
        disabled={disabled || membersOnly}
        data-pulse-card
        onClick={(event) => {
          addToCartFeedback(event.currentTarget);
          onAdd();
          setAdded((count) => count + 1);
        }}
        aria-label={membersOnly ? t('sell.passes.membersOnlyNamed', { name: terms.name }) : t('sell.passes.add', { name: terms.name, price: format.money(terms.price) })}
      >
        <span className="console-sell-pass__band" aria-hidden="true">
          <span className="console-sell-pass__icon">
            <NavIcon name="passes" />
          </span>
          <span className="console-sell-pass__duration console-num">{passDuration(t, terms.durationSeconds)}</span>
          {quantity > 0 ? (
            <span key={added} className="console-sell-card__qty console-num">
              {t('sell.inCartShort', { count: quantity })}
            </span>
          ) : null}
        </span>
        <span className="console-sell-card__body" aria-hidden="true">
          <span className="console-sell-card__name">{terms.name}</span>
          <span className="console-sell-pass__when">
            <Icon name="clock" />
            {windowSummary(t, terms.windows)}
          </span>
          <span className="console-sell-pass__meta console-subtle">
            {validitySummary(t, terms.validity)}
            {limit === null ? null : ` · ${limit}`}
          </span>
          <span className="console-sell-card__foot">
            <span className="console-sell-card__price console-num">{format.money(terms.price)}</span>
            <span className="console-sell-pass__player">
              {membersOnly ? null : <Icon name="user" />}
              {membersOnly ? t('sell.passes.membersOnly') : t('sell.passes.needsPlayerShort')}
            </span>
          </span>
        </span>
      </button>
    </div>
  );
}

function CartRow({ line, guest, leaving = false, onQuantity }: { readonly line: CartLine; readonly guest: boolean; readonly leaving?: boolean; readonly onQuantity: (quantity: number) => void }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const lineTotal: Money = { amount: line.unitPrice.amount * line.quantity, currency: line.unitPrice.currency };
  if (line.passProductId !== undefined) {
    // One pass per line: no quantity stepper, a remove button instead.
    return (
      <li className={cx('console-cart-line', leaving ? 'clav-line-leave' : 'clav-line-enter')} aria-hidden={leaving || undefined} inert={leaving || undefined}>
        <span className="console-cart-line__text">
          <span className="console-cart-line__name">{line.name}</span>
          <span className="console-subtle">{t(guest ? 'sell.passes.guestLineDetail' : 'sell.passes.lineDetail', { duration: line.detail ?? '' })}</span>
        </span>
        <span className="console-cart-line__qty">
          <Button size="sm" variant="ghost" label={t('sell.passes.remove', { name: line.name })} icon={<span aria-hidden="true">×</span>} onClick={() => onQuantity(0)} />
        </span>
        <span className="console-cart-line__price console-num">{format.money(lineTotal)}</span>
      </li>
    );
  }
  return (
    <li className={cx('console-cart-line', leaving ? 'clav-line-leave' : 'clav-line-enter')} aria-hidden={leaving || undefined} inert={leaving || undefined}>
      <span className="console-cart-line__text">
        <span className="console-cart-line__name">{line.name}</span>
        {line.detail === undefined ? null : <span className="console-subtle">{line.detail}</span>}
        <span className="console-subtle console-num">{t('sell.cart.each', { price: format.money(line.unitPrice) })}</span>
      </span>
      <span className="console-cart-line__qty">
        <Button size="sm" variant="ghost" label={t('sell.cart.less', { name: line.name })} icon={<span aria-hidden="true">−</span>} onClick={() => onQuantity(line.quantity - 1)} />
        <span className="console-num" aria-live="polite">
          <RollingNumber value={line.quantity} format={(n) => String(n)} durationMs={160} />
        </span>
        <Button size="sm" variant="ghost" label={t('sell.cart.more', { name: line.name })} icon={<Icon name="plus" />} onClick={() => onQuantity(line.quantity + 1)} />
      </span>
      <span className="console-cart-line__price console-num">{format.money(lineTotal)}</span>
    </li>
  );
}

/**
 * The player the sale is for (spec/06 §8.3 step 1): search the organisation's players and pick one, or
 * leave it anonymous. A player makes the Wallet and On account tenders available at checkout.
 */
function PlayerField({
  organizationId,
  player,
  onChange,
}: {
  readonly organizationId: string;
  readonly player: OrganizationPlayer | null;
  readonly onChange: (player: OrganizationPlayer | null) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const [q, setQ] = useState('');
  const search = usePlayerSearch(organizationId, q, { enabled: player === null && q.trim().length >= PLAYER_SEARCH_MIN });

  if (player !== null) {
    return (
      <div className="console-player-chosen">
        <Avatar name={playerName(player)} size="md" decorative />
        <span className="console-player-chosen__name">{playerName(player)}</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t('sell.cart.playerRemove', { name: playerName(player) })}
          onClick={() => {
            onChange(null);
            setQ('');
          }}
        >
          {t('sell.cart.playerChange')}
        </Button>
      </div>
    );
  }
  return (
    <div className="console-player-pick">
      <SearchField label={t('sell.cart.player')} placeholder={t('sell.cart.playerHint')} value={q} onChange={(event) => setQ(event.target.value)} />
      {search.isFetching ? <Skeleton shape="text" lines={2} /> : null}
      {q.trim().length < PLAYER_SEARCH_MIN ? <p className="console-subtle">{t('sell.cart.playerNone')}</p> : null}
      <ul className="console-player-pick__results">
        {pickerResults(search, q).map((candidate) => (
          <li key={candidate.id}>
            <button type="button" className="console-player-pick__result" onClick={() => onChange(candidate)}>
              <Avatar name={playerName(candidate)} size="sm" decorative />
              <span className="console-player-pick__name">{playerName(candidate)}</span>
              {candidate.phoneNumber === null ? null : <span className="console-subtle">{candidate.phoneNumber}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The "Deliver to station" select — the `To PC ##` on the cart (spec/13 §4). */
function StationField({ stationId, onChange }: { readonly stationId: string; readonly onChange: (id: string) => void }): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const stations = useStations(venue.organizationId, venue.id);
  const rows = (stations.data?.pages ?? []).flatMap((page) => page.data);
  const options = [{ value: '', label: t('sell.cart.noStation') }, ...rows.map((station) => ({ value: station.id, label: t('sell.cart.toStation', { number: station.number }) }))];
  return <SelectField label={t('sell.cart.deliverTo')} placeholder={t('sell.cart.noStation')} options={options} value={stationId} onValueChange={onChange} />;
}

interface CustomLineState {
  readonly kind: 'time' | 'retail' | 'other';
  readonly name: string;
  readonly price: string;
}

/** A line that is not on the menu — time, a retail item, or anything else (a pass is a pass line, under Passes). */
function CustomLineForm({ currency, onAdd }: { readonly currency: string; readonly onAdd: (line: Omit<CartLine, 'key'>) => void }): ReactNode {
  const { t } = useTranslation('console');
  const empty: CustomLineState = { kind: 'time', name: '', price: '' };
  const [form, setForm] = useState<CustomLineState>(empty);
  const [errors, setErrors] = useState<{ name?: string; price?: string }>({});

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: { name?: string; price?: string } = {};
    if (form.name.trim() === '') next.name = t('sell.custom.required');
    const price = moneyFrom(form.price, currency);
    if (price === null) next.price = t('sell.custom.priceInvalid');
    setErrors(next);
    if (Object.keys(next).length > 0 || price === null) return;
    onAdd({ kind: form.kind, name: form.name.trim(), unitPrice: price, quantity: 1 });
    setForm(empty);
    setErrors({});
  }

  return (
    <form className="console-form" aria-label={t('sell.custom.title')} noValidate onSubmit={submit}>
      <SegmentedControl<CustomLineState['kind']>
        label={t('sell.custom.kind')}
        value={form.kind}
        onValueChange={(kind) => setForm((c) => ({ ...c, kind }))}
        block
        options={(['time', 'retail', 'other'] as const).map((kind) => ({ value: kind, label: t(`sell.kind.${kind}`) }))}
      />
      <TextField label={t('sell.custom.name')} required value={form.name} onChange={(event) => setForm((c) => ({ ...c, name: event.target.value }))} error={errors.name} />
      <TextField label={t('sell.custom.price', { currency })} required numeric inputMode="decimal" value={form.price} onChange={(event) => setForm((c) => ({ ...c, price: event.target.value }))} error={errors.price} />
      <div className="console-form__actions console-form__actions--end">
        <Button type="submit" variant="primary" icon={<Icon name="plus" />}>
          {t('sell.custom.submit')}
        </Button>
      </div>
    </form>
  );
}

